import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx } from '../lib/cx.js';
import type { ButtonVariant, ControlSize } from './Button.js';
import { Tooltip } from './Tooltip.js';
import './button.css';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Required: icon-only controls need an accessible name. Also used as the tooltip. */
  label: string;
  icon: ReactNode;
  variant?: Exclude<ButtonVariant, 'hero'>;
  size?: ControlSize;
  /** Keyboard shortcut shown in the tooltip, e.g. ['Ctrl', 'K']. */
  shortcut?: readonly string[];
  /** Set false to suppress the tooltip. */
  tooltip?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    label,
    icon,
    variant = 'ghost',
    size = 'md',
    shortcut,
    tooltip = true,
    className,
    type,
    ...rest
  },
  ref,
) {
  const button = (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      className={cx('vel-btn', className)}
      data-variant={variant}
      data-size={size}
      data-icon-only=""
      {...rest}
    >
      <span className="vel-btn__icon" aria-hidden>
        {icon}
      </span>
    </button>
  );
  if (!tooltip) return button;
  return (
    <Tooltip content={label} {...(shortcut ? { shortcut } : {})}>
      {button}
    </Tooltip>
  );
});
